import { useNavigate } from 'react-router-dom'
import { Box, Typography, Paper, Alert, TextField, Button, Stack, Card, CardContent, CardActionArea, Chip, Fade, Avatar, CircularProgress, Stepper, Step, StepLabel, useTheme, useMediaQuery, alpha, ToggleButtonGroup, ToggleButton } from '@mui/material'
import { useCreateAdventure } from '../hooks/useAdventures'
import { useState, useRef, useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Save, Cancel, ArrowBack, ArrowForward, CheckCircle, ChevronLeft, ChevronRight, Person, Group as GroupIcon, RocketLaunch, Category, Edit as EditIcon, GridView, ViewList } from '@mui/icons-material'
import { CreateAdventureRequest, AdventureCategoryDto } from '@api/types/adventure'
import { useQuery } from '@tanstack/react-query'
import { socialApi } from '@api/endpoints/social'
import { adventuresApi } from '@api/endpoints/adventures'
import { GroupDto, GroupRole } from '@api/types/social'
import { useAuthStore } from '@store/authStore'
import CategoryComparisonTable from '../components/CategoryComparisonTable'

// Step configuration for the wizard
const steps = [
  { label: 'Creator', icon: Person },
  { label: 'Category', icon: Category },
  { label: 'Details', icon: EditIcon },
]

const createAdventureSchema = z.object({
  title: z.string().min(3, 'Title must be at least 3 characters').max(100, 'Title is too long'),
  quickDescription: z.string().min(10, 'Description must be at least 10 characters').max(500, 'Description is too long'),
})

type CreateAdventureFormData = z.infer<typeof createAdventureSchema>

export default function AdventureCreatePage() {
  const navigate = useNavigate()
  const createMutation = useCreateAdventure()
  const [error, setError] = useState<string | null>(null)
  const [step, setStep] = useState<'owner' | 'type' | 'details'>('owner')
  const [selectedType, setSelectedType] = useState<number | null>(null)
  const [selectedOwner, setSelectedOwner] = useState<{ type: 'personal' | 'group'; groupId?: string; groupName?: string } | null>(null)
  const [categoryViewMode, setCategoryViewMode] = useState<'cards' | 'table'>('cards')
  const user = useAuthStore((state) => state.user)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // Get current step index for stepper
  const getStepIndex = () => {
    switch (step) {
      case 'owner': return 0
      case 'type': return 1
      case 'details': return 2
      default: return 0
    }
  }

  // Fetch user's groups to check for owner/admin roles
  const { data: myGroups, isLoading: isLoadingGroups } = useQuery({
    queryKey: ['my-groups-for-create'],
    queryFn: () => socialApi.getMyGroups(),
    staleTime: 60000,
  })

  // Fetch adventure categories from API
  const { data: categories, isLoading: isLoadingCategories } = useQuery({
    queryKey: ['adventure-categories'],
    queryFn: () => adventuresApi.getCategories(),
    staleTime: 300000, // 5 minutes
  })

  // Fetch criteria types (only when table view is active)
  const { data: criteriaTypes = [] } = useQuery({
    queryKey: ['criteria-types'],
    queryFn: () => adventuresApi.getCriteriaTypes(),
    staleTime: 300000,
    enabled: categoryViewMode === 'table',
  })

  // Fetch node types (only when table view is active)
  const { data: nodeTypes = [] } = useQuery({
    queryKey: ['node-types'],
    queryFn: () => adventuresApi.getNodeTypes(),
    staleTime: 300000,
    enabled: categoryViewMode === 'table',
  })

  // Filter and sort active categories
  // Categories must be active AND user must have required flags (bitwise check)
  const activeCategories = (categories || [])
    .filter((cat: AdventureCategoryDto) => {
      // Must be active
      if (!cat.isActive) return false
      // If no flags required (0 or null/undefined), category is available to everyone
      if (!cat.requiredUserFlags) return true
      // Check if user has all required flags using bitwise AND
      const userFlags = user?.userFlags ?? 0
      return (userFlags & cat.requiredUserFlags) === cat.requiredUserFlags
    })
    .sort((a: AdventureCategoryDto, b: AdventureCategoryDto) => a.sortOrder - b.sortOrder)

  // Filter groups where user is owner or admin (maintainer)
  const eligibleGroups = (myGroups || []).filter((group: GroupDto) =>
    group.isOwner || group.userRole === GroupRole.Owner || group.userRole === GroupRole.Admin
  )

  // Auto-skip owner step if no eligible groups
  useEffect(() => {
    if (!isLoadingGroups && step === 'owner' && eligibleGroups.length === 0) {
      // No eligible groups, auto-select personal and move to type selection
      setSelectedOwner({ type: 'personal' })
      setStep('type')
    }
  }, [isLoadingGroups, eligibleGroups.length, step])

  // Drag scroll state
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return
    setIsDragging(true)
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft)
    setScrollLeft(scrollContainerRef.current.scrollLeft)
  }, [])

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return
    e.preventDefault()
    const x = e.pageX - scrollContainerRef.current.offsetLeft
    const walk = (x - startX) * 1.5 // Scroll speed multiplier
    scrollContainerRef.current.scrollLeft = scrollLeft - walk
  }, [isDragging, startX, scrollLeft])

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false)
  }, [])

  // Scroll indicator state
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(true)

  const updateScrollIndicators = useCallback(() => {
    if (!scrollContainerRef.current) return
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
    setCanScrollLeft(scrollLeft > 10)
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10)
  }, [])

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    updateScrollIndicators()
    container.addEventListener('scroll', updateScrollIndicators)
    window.addEventListener('resize', updateScrollIndicators)

    return () => {
      container.removeEventListener('scroll', updateScrollIndicators)
      window.removeEventListener('resize', updateScrollIndicators)
    }
  }, [updateScrollIndicators])

  const scrollByAmount = (direction: 'left' | 'right') => {
    if (!scrollContainerRef.current) return
    const scrollAmount = 300
    scrollContainerRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    })
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateAdventureFormData>({
    resolver: zodResolver(createAdventureSchema),
    defaultValues: {
      title: '',
      quickDescription: '',
    },
  })

  const handleOwnerSelect = (type: 'personal' | 'group', group?: GroupDto) => {
    if (type === 'personal') {
      setSelectedOwner({ type: 'personal' })
    } else if (group) {
      setSelectedOwner({ type: 'group', groupId: group.id, groupName: group.name })
    }
  }

  const handleOwnerContinue = () => {
    if (selectedOwner) {
      setStep('type')
    }
  }

  const onSubmit = async (data: CreateAdventureFormData) => {
    if (selectedType === null) {
      setError('Please select an adventure type')
      setStep('type')
      return
    }

    setError(null)
    try {
      const requestData: CreateAdventureRequest = {
        title: data.title,
        description: data.quickDescription,
        quickDescription: data.quickDescription,
        location: '',
        startDate: null,
        endDate: null,
        latitude: null,
        longitude: null,
        categoryId: selectedType,
        isRepeatable: false,
        isPublic: true,
        allowGroups: true,
        maxParticipants: 0,
        difficultyLevel: 1,
        useDisplayForJoining: false,
        ownerGroupId: selectedOwner?.type === 'group' ? selectedOwner.groupId : null,
      }

      const response = await createMutation.mutateAsync(requestData)
      const adventureId = response.data?.id || (response as any).id
      if (adventureId) {
        navigate(`/adventures/${adventureId}/edit`)
      } else {
        setError('Adventure created but unable to navigate to editor.')
        navigate('/my-adventures')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create adventure. Please try again.')
    }
  }

  const handleCancel = () => {
    navigate('/adventures')
  }

  const handleTypeSelect = (typeId: number) => {
    setSelectedType(typeId)
  }

  const handleContinue = () => {
    if (selectedType !== null) {
      setStep('details')
    }
  }

  const handleBack = () => {
    if (step === 'details') {
      setStep('type')
    } else if (step === 'type' && eligibleGroups.length > 0) {
      setStep('owner')
    }
  }

  const selectedTypeInfo = activeCategories.find((cat: AdventureCategoryDto) => cat.id === selectedType)

  // Allow wider layout when showing table view on category step
  const useWideLayout = step === 'type' && categoryViewMode === 'table'

  return (
    <Box sx={{ maxWidth: useWideLayout ? 'none' : 900, mx: 'auto', px: useWideLayout ? 2 : 0 }}>
      {/* Hero Header */}
      <Paper
        elevation={0}
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          borderRadius: 3,
          p: { xs: 3, md: 4 },
          mb: 3,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          maxWidth: useWideLayout ? 900 : 'none',
          mx: useWideLayout ? 'auto' : 0,
        }}
      >
        {/* Background decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: alpha(theme.palette.primary.main, 0.1),
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: alpha(theme.palette.secondary.main, 0.1),
          }}
        />

        {/* Icon */}
        <Avatar
          sx={{
            width: 72,
            height: 72,
            mx: 'auto',
            mb: 2,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
          }}
        >
          <RocketLaunch sx={{ fontSize: 36 }} />
        </Avatar>

        <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700} gutterBottom>
          Create New Adventure
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto' }}>
          Design an unforgettable experience for explorers to discover
        </Typography>
      </Paper>

      {/* Step Indicator */}
      <Box sx={{ maxWidth: useWideLayout ? 900 : 'none', mx: useWideLayout ? 'auto' : 0 }}>
        <Stepper
          activeStep={getStepIndex()}
          alternativeLabel={!isMobile}
          orientation={isMobile ? 'horizontal' : 'horizontal'}
          sx={{
            mb: 4,
            '& .MuiStepLabel-label': {
              mt: 1,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            },
            '& .MuiStepIcon-root': {
              width: { xs: 28, sm: 36 },
              height: { xs: 28, sm: 36 },
            },
            '& .MuiStepIcon-root.Mui-active': {
              color: 'primary.main',
            },
            '& .MuiStepIcon-root.Mui-completed': {
              color: 'success.main',
            },
          }}
        >
          {steps.map((s, index) => (
            <Step key={s.label} completed={getStepIndex() > index}>
              <StepLabel>{s.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Owner Selection Step */}
      {step === 'owner' && (
        <Fade in>
          <Box>
            {isLoadingGroups ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ textAlign: 'center', mb: 1 }}>
                  Who will own this adventure?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 4 }}>
                  Choose whether to create under your personal account or on behalf of a group
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: eligibleGroups.length > 0 ? 'repeat(2, 1fr)' : '1fr' },
                    gap: 2,
                    maxWidth: eligibleGroups.length > 0 ? 700 : 400,
                    mx: 'auto',
                  }}
                >
                  {/* Personal option */}
                  <Card
                    elevation={selectedOwner?.type === 'personal' ? 8 : 1}
                    sx={{
                      border: 2,
                      borderColor: selectedOwner?.type === 'personal' ? 'primary.main' : 'transparent',
                      borderRadius: 3,
                      transition: 'all 0.3s ease-in-out',
                      transform: selectedOwner?.type === 'personal' ? 'scale(1.02)' : 'scale(1)',
                      '&:hover': {
                        transform: 'scale(1.02)',
                        boxShadow: 6,
                      },
                    }}
                  >
                    <CardActionArea onClick={() => handleOwnerSelect('personal')} sx={{ height: '100%' }}>
                      <CardContent sx={{ textAlign: 'center', py: 4 }}>
                        <Avatar
                          src={user?.avatar || undefined}
                          sx={{
                            width: 64,
                            height: 64,
                            mx: 'auto',
                            mb: 2,
                            bgcolor: 'primary.main',
                            border: selectedOwner?.type === 'personal' ? 3 : 0,
                            borderColor: 'primary.light',
                          }}
                        >
                          <Person sx={{ fontSize: 32 }} />
                        </Avatar>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          As Myself
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {user?.displayName || user?.username || 'Personal Account'}
                        </Typography>
                        {selectedOwner?.type === 'personal' && (
                          <CheckCircle
                            color="primary"
                            sx={{
                              position: 'absolute',
                              top: 12,
                              right: 12,
                              fontSize: 28,
                            }}
                          />
                        )}
                      </CardContent>
                    </CardActionArea>
                  </Card>

                  {/* Group options */}
                  {eligibleGroups.map((group: GroupDto) => (
                    <Card
                      key={group.id}
                      elevation={selectedOwner?.groupId === group.id ? 8 : 1}
                      sx={{
                        border: 2,
                        borderColor: selectedOwner?.groupId === group.id ? 'secondary.main' : 'transparent',
                        borderRadius: 3,
                        transition: 'all 0.3s ease-in-out',
                        transform: selectedOwner?.groupId === group.id ? 'scale(1.02)' : 'scale(1)',
                        position: 'relative',
                        '&:hover': {
                          transform: 'scale(1.02)',
                          boxShadow: 6,
                        },
                      }}
                    >
                      <CardActionArea onClick={() => handleOwnerSelect('group', group)} sx={{ height: '100%' }}>
                        <CardContent sx={{ textAlign: 'center', py: 4 }}>
                          <Avatar
                            src={group.groupImage || undefined}
                            sx={{
                              width: 64,
                              height: 64,
                              mx: 'auto',
                              mb: 2,
                              bgcolor: 'secondary.main',
                              border: selectedOwner?.groupId === group.id ? 3 : 0,
                              borderColor: 'secondary.light',
                            }}
                          >
                            <GroupIcon sx={{ fontSize: 32 }} />
                          </Avatar>
                          <Typography variant="h6" fontWeight={600} gutterBottom noWrap>
                            {group.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {group.memberCount} member{group.memberCount !== 1 ? 's' : ''}
                          </Typography>
                          {selectedOwner?.groupId === group.id && (
                            <CheckCircle
                              color="secondary"
                              sx={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                fontSize: 28,
                              }}
                            />
                          )}
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  ))}
                </Box>

                {/* Navigation Buttons */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    size="large"
                    sx={{ minWidth: 120 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleOwnerContinue}
                    endIcon={<ArrowForward />}
                    disabled={!selectedOwner}
                    size="large"
                    sx={{ minWidth: 120 }}
                  >
                    Continue
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Fade>
      )}

      {step === 'type' && (
        <Fade in>
          <Box>
            {/* Selected Owner Indicator */}
            {selectedOwner && (
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  backgroundColor: alpha(selectedOwner.type === 'personal' ? theme.palette.primary.main : theme.palette.secondary.main, 0.1),
                  borderRadius: 2,
                  border: 1,
                  borderColor: alpha(selectedOwner.type === 'personal' ? theme.palette.primary.main : theme.palette.secondary.main, 0.3),
                }}
              >
                <Avatar
                  src={selectedOwner.type === 'personal' ? (user?.avatar || undefined) : undefined}
                  sx={{
                    bgcolor: selectedOwner.type === 'personal' ? 'primary.main' : 'secondary.main',
                    width: 36,
                    height: 36,
                  }}
                >
                  {selectedOwner.type === 'personal' ? <Person fontSize="small" /> : <GroupIcon fontSize="small" />}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Creating as: {selectedOwner.type === 'personal' ? (user?.displayName || user?.username || 'Myself') : selectedOwner.groupName}
                  </Typography>
                </Box>
                {eligibleGroups.length > 0 && (
                  <Button size="small" variant="text" onClick={() => setStep('owner')}>
                    Change
                  </Button>
                )}
              </Paper>
            )}

            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ textAlign: 'center', mb: 1 }}>
              What type of adventure?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 2 }}>
              Choose a category that best fits your adventure
            </Typography>

            {/* View Mode Toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <ToggleButtonGroup
                value={categoryViewMode}
                exclusive
                onChange={(_, value) => value && setCategoryViewMode(value)}
                size="small"
              >
                <ToggleButton value="cards">
                  <GridView sx={{ mr: 0.5 }} fontSize="small" />
                  Cards
                </ToggleButton>
                <ToggleButton value="table">
                  <ViewList sx={{ mr: 0.5 }} fontSize="small" />
                  Table
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Type Selection */}
          {isLoadingCategories ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : categoryViewMode === 'cards' ? (
            /* Cards View */
            <Box>
              <Box
                ref={scrollContainerRef}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                sx={{
                  display: 'flex',
                  gap: 2,
                  overflowX: 'auto',
                  pb: 2,
                  px: 1,
                  scrollSnapType: 'x mandatory',
                  cursor: isDragging ? 'grabbing' : 'grab',
                  userSelect: 'none',
                  // Hide scrollbar but keep scroll functionality
                  scrollbarWidth: 'none', // Firefox
                  msOverflowStyle: 'none', // IE/Edge
                  '&::-webkit-scrollbar': {
                    display: 'none', // Chrome/Safari
                  },
                }}
              >
              {activeCategories.map((category: AdventureCategoryDto) => {
                const isSelected = selectedType === category.id

                return (
                  <Card
                    key={category.id}
                    sx={{
                      minWidth: 280,
                      maxWidth: 280,
                      scrollSnapAlign: 'start',
                      border: isSelected ? 2 : 1,
                      borderColor: isSelected ? category.color : 'divider',
                      backgroundColor: isSelected ? alpha(category.color, 0.08) : 'background.paper',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        borderColor: category.color,
                        transform: 'translateY(-2px)',
                        boxShadow: 3,
                      },
                    }}
                  >
                    <CardActionArea onClick={() => handleTypeSelect(category.id)} sx={{ height: '100%' }}>
                      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          <Typography component="span" sx={{ fontSize: 28 }}>
                            {category.iconName}
                          </Typography>
                          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                            {category.displayName}
                          </Typography>
                          {isSelected && (
                            <CheckCircle sx={{ color: category.color, fontSize: 20, ml: 'auto' }} />
                          )}
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flex: 1 }}>
                          {category.description}
                        </Typography>

                        {category.features && category.features.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 'auto' }}>
                            {category.features.map((feature) => (
                              <Chip
                                key={feature}
                                label={feature}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontSize: '0.7rem',
                                  height: 22,
                                  borderColor: isSelected ? category.color : 'divider',
                                  color: isSelected ? category.color : 'text.secondary',
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </CardContent>
                    </CardActionArea>
                  </Card>
                )
              })}
              </Box>
            </Box>
          ) : (
            /* Table View */
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <CategoryComparisonTable
                categories={activeCategories}
                criteriaTypes={criteriaTypes}
                nodeTypes={nodeTypes}
                selectedCategoryId={selectedType}
                onCategorySelect={handleTypeSelect}
              />
            </Box>
          )}

          {/* Navigation and Buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: categoryViewMode === 'cards' ? 'space-between' : 'center', mt: 3 }}>
            {/* Left scroll arrow - only show in cards view */}
            {categoryViewMode === 'cards' && (
              <Button
                variant="outlined"
                onClick={() => scrollByAmount('left')}
                disabled={!canScrollLeft}
                sx={{ minWidth: 40, px: 1 }}
              >
                <ChevronLeft />
              </Button>
            )}

            {/* Center buttons */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                startIcon={<Cancel />}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleContinue}
                endIcon={<ArrowForward />}
                disabled={selectedType === null}
              >
                Continue
              </Button>
            </Box>

            {/* Right scroll arrow - only show in cards view */}
            {categoryViewMode === 'cards' && (
              <Button
                variant="outlined"
                onClick={() => scrollByAmount('right')}
                disabled={!canScrollRight}
                sx={{ minWidth: 40, px: 1 }}
              >
                <ChevronRight />
              </Button>
            )}
          </Box>
          </Box>
        </Fade>
      )}

      {step === 'details' && (
        <Fade in>
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ textAlign: 'center', mb: 1 }}>
              Adventure Details
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
              Give your {selectedTypeInfo?.displayName.toLowerCase()} a name and brief description
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Paper elevation={2} sx={{ p: 3, borderRadius: 3, maxWidth: 600, mx: 'auto' }}>
              <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                <Stack spacing={3}>
                  {/* Selected Type Indicator */}
                  {selectedTypeInfo && (
                    <Paper
                      elevation={0}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        p: 1.5,
                        backgroundColor: alpha(selectedTypeInfo.color, 0.1),
                        borderRadius: 2,
                        border: 1,
                        borderColor: alpha(selectedTypeInfo.color, 0.3),
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          bgcolor: selectedTypeInfo.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 20,
                        }}
                      >
                        {selectedTypeInfo.iconName}
                      </Box>
                      <Typography variant="body2" fontWeight={600}>
                        {selectedTypeInfo.displayName}
                      </Typography>
                      <Button size="small" variant="text" onClick={handleBack} sx={{ ml: 'auto' }}>
                        Change
                      </Button>
                    </Paper>
                  )}

                <TextField
                  {...register('title')}
                  label="Adventure Title"
                  fullWidth
                  error={!!errors.title}
                  helperText={errors.title?.message}
                  required
                  placeholder="e.g., Hidden Waterfall Trail"
                />

                <TextField
                  {...register('quickDescription')}
                  label="Quick Description"
                  fullWidth
                  multiline
                  rows={4}
                  error={!!errors.quickDescription}
                  helperText={errors.quickDescription?.message || 'Provide a brief overview of your adventure'}
                  required
                  placeholder="Describe what makes this adventure special..."
                />

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', pt: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    startIcon={<ArrowBack />}
                    disabled={createMutation.isPending}
                    size="large"
                    sx={{ minWidth: 120 }}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<RocketLaunch />}
                    disabled={createMutation.isPending}
                    size="large"
                    sx={{ minWidth: 160 }}
                  >
                    {createMutation.isPending ? 'Creating...' : 'Create Adventure'}
                  </Button>
                </Box>
                </Stack>
              </Box>
            </Paper>
          </Box>
        </Fade>
      )}
    </Box>
  )
}
